import * as THREE from 'three';
import { blankFace, glyphFace, type GlyphFace } from './hud-materials.js';
import { copiesGeometry, polygonFrameGeometry, polygonGeometry, unpickable } from './hud-parts.js';

/** A glyph that has arrived lights its face from within this warm. */
const LIT = 0x6a4a1c;

/** A button's outer half-width in CSS pixels at size 1, the same as a radial menu socket. */
export const BUTTON = 22;

/** Shared geometry, materials and drawn glyph faces for every button: building them per button would waste memory and draw calls' state changes. */
export class ButtonKit {
  readonly face = polygonGeometry(8, BUTTON * .86);
  readonly rim = polygonFrameGeometry(8, BUTTON * 1.08, BUTTON * .84, 3);
  readonly back = new THREE.CylinderGeometry(BUTTON * 1.06, BUTTON * 1.16, 8, 8, 1, false, Math.PI / 8).rotateX(Math.PI / 2);
  /** A rivet at alternate corners of the bezel, all four in one geometry. */
  readonly rivets = copiesGeometry(new THREE.SphereGeometry(1.9, 10, 8), [0, 1, 2, 3].map(k => {
    const a = (k * 2 + .5) / 8 * Math.PI * 2;
    return new THREE.Vector3(Math.cos(a) * BUTTON * .97, Math.sin(a) * BUTTON * .97, 4.5);
  }));
  /** A polished gilt bezel round a blackened iron cup: the same make as the radial menu's buttons. */
  readonly bezel = new THREE.MeshStandardMaterial({ color: 0xd4a95c, metalness: .95, roughness: .22, emissive: 0x2a1c08, emissiveIntensity: .35 });
  readonly iron = new THREE.MeshStandardMaterial({ color: 0x1b1a19, metalness: .75, roughness: .32 });
  private faces = new Map<string, Promise<GlyphFace | null>>();
  constructor(readonly glow: THREE.Texture | null) {}
  /**
   * The glyph face for an icon in a field and ink, drawn on first need and shared by every button and menu entry that shows it:
   * drawing one is a few milliseconds of pixel work and two texture uploads. Null where it cannot be drawn.
   */
  glyph(icon: string, field: number, ink: number, raised = true, margin = .16): Promise<GlyphFace | null> {
    const key = `${icon}|${field}|${ink}|${raised}|${margin}`;
    let face = this.faces.get(key);
    if (!face) { face = glyphFace(icon, field, ink, raised, margin).catch(() => null); this.faces.set(key, face); }
    return face;
  }
  dispose() {
    for (const item of [this.face, this.rim, this.back, this.rivets, this.bezel, this.iron]) item.dispose();
    for (const face of this.faces.values()) void face.then(texture => texture?.dispose());
    this.faces.clear();
  }
}

/**
 * One riveted octagonal button with a glyph raised from its enamel face. It lifts toward the pointer, glows from behind while
 * `pressed` (an open panel, a held state), dims while `disabled`, and flashes when `flash()` is called. `setIcon` swaps its glyph
 * (Pause and Resume share a button).
 */
export class OctagonButton extends THREE.Group {
  hovered = false; pressed = false; disabled = false;
  // Blank maps until the glyph is drawn: the shader is the glyph variant from the first frame, so its face arriving links nothing.
  private material = new THREE.MeshStandardMaterial({ color: 0x1c140c, metalness: .2, roughness: .38, emissive: 0x000000, emissiveIntensity: .55, map: blankFace().map, normalMap: blankFace().normalMap, emissiveMap: blankFace().map });
  private painted = false;
  private glow: THREE.Sprite | null;
  private icon = '';
  private lift = 0; private flashing = 0; private shine = 0;

  constructor(readonly key: string, icon: string, private tint: number, private kit: ButtonKit, glowColor = 0xffc46b) {
    super();
    this.userData.button = key;
    const back = new THREE.Mesh(kit.back, kit.iron); back.position.z = -4;
    const rim = new THREE.Mesh(kit.rim, kit.bezel); rim.position.z = .5;
    const face = new THREE.Mesh(kit.face, this.material); face.position.z = 1;
    this.add(back, rim, face, new THREE.Mesh(kit.rivets, kit.bezel));
    this.glow = kit.glow ? unpickable(new THREE.Sprite(new THREE.SpriteMaterial({ map: kit.glow, color: glowColor, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 }))) : null;
    if (this.glow) { this.glow.scale.setScalar(BUTTON * 4.2); this.glow.position.z = -8; this.add(this.glow); }
    this.setIcon(icon);
  }

  /** Shows another glyph; each is drawn once and kept. */
  setIcon(icon: string) {
    if (icon === this.icon) return;
    this.icon = icon;
    void this.kit.glyph(icon, 0x1c140c, this.tint).then(texture => {
      if (!texture || this.icon !== icon) return;
      const { material } = this;
      material.map = material.emissiveMap = texture.map; material.normalMap = texture.normalMap; material.emissive.setHex(LIT); this.painted = true;
    });
  }
  flash() { this.flashing = 1; }

  update(dt: number, time: number) {
    this.lift += ((this.hovered && !this.disabled ? 1 : this.pressed ? .5 : 0) - this.lift) * Math.min(1, dt * 14);
    this.flashing = Math.max(0, this.flashing - dt * 3);
    this.shine += ((this.pressed ? 1 : 0) - this.shine) * Math.min(1, dt * 10);
    this.scale.setScalar(1 + this.lift * .1 + this.flashing * .15);
    this.position.z = this.lift * 10;
    this.material.color.setScalar((this.painted ? this.disabled ? .38 : 1 : .11) + this.flashing * .9);
    if (this.glow) (this.glow.material as THREE.SpriteMaterial).opacity = Math.max(this.shine * (.75 + Math.sin(time * 4) * .1), this.flashing * .8);
  }

  dispose() {
    this.material.dispose(); (this.glow?.material as THREE.Material | undefined)?.dispose();
  }
}
